/* global Element, HTMLFormElement */
/**
 * form-a11y-validation.js
 *
 * Accessible client-side validation for storefront forms (WCAG 2.1 AA 3.3.1 / 3.3.3 / 4.1.3).
 *
 * Native constraint-validation bubbles ("Fill out this field") are not reliably announced by
 * VoiceOver and do not leave a persistent, programmatically associated error. Opt a form in
 * with the `data-a11y-validate` attribute and this helper, when JS runs:
 *   - sets `novalidate` so the native bubble is suppressed (no-JS users keep native validation);
 *   - on an invalid submit, blocks the submit, marks each invalid control `aria-invalid="true"`,
 *     renders an inline error wired through `aria-describedby` (existing ids are preserved),
 *     and moves focus to the first invalid control so its error is read;
 *   - clears a control's error state as soon as the user edits it;
 *   - lets a valid submit through untouched, so other submit handlers still run.
 *
 * Safe to execute more than once (duplicate same-URL <script> tags re-execute): the whole file
 * is an IIFE guarded by a window flag. Every DOM access on a <form> borrows from the prototype,
 * because named controls shadow form properties (see known-footguns.md).
 */
(function () {
  'use strict';

  if (window.__mdFormA11yValidation) return;
  window.__mdFormA11yValidation = true;

  var ATTR = 'data-a11y-validate';
  var ERROR_CLASS = 'form-a11y-error';
  var ERROR_ID_ATTR = 'data-a11y-error-id';
  var OWNS_INVALID_ATTR = 'data-a11y-invalid';
  var INSIDE_SELECTOR = '.field--with-error, .form-group';
  var AFTER_SELECTOR = '.field';

  var hasAttr = Element.prototype.hasAttribute;
  var getAttr = Element.prototype.getAttribute;
  var setAttr = Element.prototype.setAttribute;
  var removeAttr = Element.prototype.removeAttribute;
  var qsa = Element.prototype.querySelectorAll;
  var elementsGetter = Object.getOwnPropertyDescriptor(
    HTMLFormElement.prototype,
    'elements'
  ).get;

  var DEFAULTS = {
    formFieldRequired: 'This field is required.',
    formEmailInvalid: 'Enter a valid email address.',
    formFieldInvalid: 'Enter a valid value.',
  };

  var uid = 0;

  function str(key) {
    var s = window.accessibilityStrings;
    return (s && typeof s[key] === 'string' && s[key]) || DEFAULTS[key];
  }

  function isOptedIn(form) {
    return form instanceof HTMLFormElement && hasAttr.call(form, ATTR);
  }

  function ensureNoValidate(form) {
    if (!hasAttr.call(form, 'novalidate')) setAttr.call(form, 'novalidate', '');
  }

  function sweep() {
    var forms = qsa.call(document.documentElement, 'form[' + ATTR + ']');
    for (var i = 0; i < forms.length; i++) ensureNoValidate(forms[i]);
  }

  function ownerForm(el) {
    return el && el.form instanceof HTMLFormElement ? el.form : null;
  }

  // Walks controls using validity (never checkValidity(), which fires `invalid` events).
  function collectInvalid(form) {
    var els = elementsGetter.call(form);
    var out = [];
    var seenRadio = {};
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.willValidate || !el.validity) continue;
      if (el.validity.valid && !ariaRequiredEmpty(el)) continue;
      if (el.type === 'radio') {
        if (seenRadio[el.name]) continue;
        seenRadio[el.name] = true;
      }
      out.push(el);
    }
    return out;
  }

  // Some forms mark fields aria-required (no native `required`, so no browser
  // bubble for no-JS visitors either), which constraint validation ignores.
  function ariaRequiredEmpty(el) {
    if (getAttr.call(el, 'aria-required') !== 'true') return false;
    if (el.type === 'checkbox' || el.type === 'radio') return false;
    return typeof el.value === 'string' && el.value.trim() === '';
  }

  function messageFor(field) {
    var v = field.validity;
    if (v.valueMissing || ariaRequiredEmpty(field))
      return str('formFieldRequired');
    if (v.typeMismatch && field.type === 'email')
      return str('formEmailInvalid');
    return field.validationMessage || str('formFieldInvalid');
  }

  function describedByTokens(field) {
    var raw = getAttr.call(field, 'aria-describedby');
    return raw ? raw.split(/\s+/).filter(Boolean) : [];
  }

  function errorIdFor(field) {
    var id = getAttr.call(field, ERROR_ID_ATTR);
    if (id) return id;
    uid += 1;
    id = (field.id ? field.id : 'form-field-' + uid) + '-a11y-error';
    setAttr.call(field, ERROR_ID_ATTR, id);
    return id;
  }

  function showError(field) {
    var id = errorIdFor(field);
    var error = document.getElementById(id);
    if (!error) {
      error = document.createElement('small');
      error.id = id;
      error.className = ERROR_CLASS + ' form__message';
      // Containers built to hold an error (Dawn `.field--with-error`, `.form-group`) get it
      // appended inside, mirroring the server-rendered error. A plain `.field` (newsletter,
      // with an absolutely positioned inline button) gets it placed right after, as the
      // server-rendered newsletter error is.
      var inside = field.closest(INSIDE_SELECTOR);
      var container = inside ? null : field.closest(AFTER_SELECTOR);
      if (inside) {
        inside.appendChild(error);
      } else if (container && container.parentNode) {
        container.parentNode.insertBefore(error, container.nextSibling);
      } else if (field.parentNode) {
        field.parentNode.appendChild(error);
      }
    }
    error.textContent = messageFor(field);

    if (getAttr.call(field, 'aria-invalid') !== 'true') {
      setAttr.call(field, 'aria-invalid', 'true');
      setAttr.call(field, OWNS_INVALID_ATTR, '');
    }
    var tokens = describedByTokens(field);
    if (tokens.indexOf(id) === -1) {
      tokens.push(id);
      setAttr.call(field, 'aria-describedby', tokens.join(' '));
    }
  }

  function clearError(field) {
    var id = getAttr.call(field, ERROR_ID_ATTR);
    if (!id) return;
    var error = document.getElementById(id);
    if (error && error.parentNode) error.parentNode.removeChild(error);
    var tokens = describedByTokens(field).filter(function (t) {
      return t !== id;
    });
    if (tokens.length)
      setAttr.call(field, 'aria-describedby', tokens.join(' '));
    else removeAttr.call(field, 'aria-describedby');
    if (hasAttr.call(field, OWNS_INVALID_ATTR)) {
      removeAttr.call(field, 'aria-invalid');
      removeAttr.call(field, OWNS_INVALID_ATTR);
    }
  }

  function report(form) {
    var invalid = collectInvalid(form);
    for (var i = 0; i < invalid.length; i++) showError(invalid[i]);
    if (invalid.length) invalid[0].focus();
    return invalid.length;
  }

  function onSubmit(event) {
    var form = event.target;
    if (!isOptedIn(form)) return;
    ensureNoValidate(form);
    // Honour a submitter that explicitly skips validation (<button formnovalidate>).
    var submitter = event.submitter;
    if (submitter && hasAttr.call(submitter, 'formnovalidate')) return;
    if (!collectInvalid(form).length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    report(form);
  }

  // Backstop for a form that received neither the load sweep nor a focusin before submit
  // (e.g. injected late and submitted programmatically with requestSubmit()): the browser
  // validates before dispatching `submit` and fires `invalid` per control instead.
  var pendingReport = [];
  function onInvalid(event) {
    var form = ownerForm(event.target);
    if (!form || !isOptedIn(form)) return;
    event.preventDefault();
    ensureNoValidate(form);
    if (pendingReport.indexOf(form) !== -1) return;
    pendingReport.push(form);
    setTimeout(function () {
      pendingReport.splice(pendingReport.indexOf(form), 1);
      report(form);
    }, 0);
  }

  function onEdit(event) {
    var field = event.target;
    if (!(field instanceof Element)) return;
    var form = ownerForm(field);
    if (!form || !isOptedIn(form)) return;
    if (field.type === 'radio' && field.name) {
      var els = elementsGetter.call(form);
      for (var i = 0; i < els.length; i++) {
        if (els[i].type === 'radio' && els[i].name === field.name)
          clearError(els[i]);
      }
      return;
    }
    clearError(field);
  }

  function onFocusIn(event) {
    var form = ownerForm(event.target);
    if (form && isOptedIn(form)) ensureNoValidate(form);
  }

  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('invalid', onInvalid, true);
  document.addEventListener('input', onEdit, true);
  document.addEventListener('change', onEdit, true);
  document.addEventListener('focusin', onFocusIn, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();
